'use client';

import { createContext, ReactNode, useContext, useEffect } from "react";
import { Exhibition, PublicationStatus } from "@/app/lib/generated/graphql-types";
import {
    useCollectionTypeEditor,
    UseCollectionTypeEditorType
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import type { ExhibitionOverviewItemEditable } from "@/app/features/sites/exhibitions/types/types";
import { ExhibitionOverviewItemSchema } from "@/app/features/sites/exhibitions/types/schema";
import { getExhibitionDetail } from "@/app/features/sites/exhibitions/data-access/queries";
import {
    exhibitionOverviewItemInputFrom,
    updateExhibitionOverviewItem
} from "@/app/features/sites/exhibitions/data-access/mutations";
import {
    useSiteExhibitionsOverviewEditor
} from "@/app/features/sites/exhibitions/contexts/SiteExhibitionsOverviewEditorContext";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

const ExhibitionOverviewItemEditorContext = createContext<UseCollectionTypeEditorType<ExhibitionOverviewItemEditable> | undefined>(undefined);

export function useExhibitionOverviewItemEditor() {
    const context = useContext(ExhibitionOverviewItemEditorContext);
    if (!context) {
        throw new Error("useExhibitionOverviewItemEditor must be used within ExhibitionOverviewItemEditorProvider");
    }
    return context;
}

function editableFromExhibition(exhibition: Exhibition): ExhibitionOverviewItemEditable {
    return {
        id: exhibition.documentId,
        title: exhibition.title,
        excerpt: exhibition.excerpt,
        promoImage: exhibition.promoImage,
    };
}

export default function ExhibitionOverviewItemEditorProvider({
    documentId,
    children,
}: {
    documentId: string;
    children: ReactNode;
}) {
    const { registerChildEditor, unregisterChildEditor } = useSiteExhibitionsOverviewEditor();
    const editor = useCollectionTypeEditor<Exhibition, ExhibitionOverviewItemEditable, Awaited<ReturnType<typeof exhibitionOverviewItemInputFrom>>>({
        documentId,
        schema: ExhibitionOverviewItemSchema,
        loadEntity: getExhibitionDetail,
        update: updateExhibitionOverviewItem,
        toEditable: editableFromExhibition,
        toInput: exhibitionOverviewItemInputFrom,
        autosave: async (targetDocumentId, input) => {
            return await updateExhibitionOverviewItem({
                documentId: targetDocumentId,
                data: input,
                status: PublicationStatus.Draft,
            });
        },
    });

    useEffect(() => {
        registerChildEditor(
            documentId,
            {
                hasChanges: editor.hasChanges,
                autosaveState: editor.autosaveState,
            },
            {
                publish: editor.publish,
                discardChanges: editor.discardChanges,
            }
        );

        return () => {
            unregisterChildEditor(documentId);
        };
    }, [
        documentId,
        editor.hasChanges,
        editor.autosaveState,
        editor.publish,
        editor.discardChanges,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <ExhibitionOverviewItemEditorContext.Provider value={editor}>
            {children}
        </ExhibitionOverviewItemEditorContext.Provider>
    )
}
